import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Edit, Trash2, Loader2, History } from "lucide-react";
import { IconButtonWithTooltip } from "@/components/ui/icon-button-tooltip";
import { toast } from "@/hooks/use-toast";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from "@/components/ui/pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useProgramStatuses } from "@/hooks/workflow/useProgramStatuses";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { workflowService } from "@/services/features/WorkflowService";
import { useAuth } from "@/contexts/AuthContext";
import { OperationsHistoryModal } from "@/v2/components/common/OperationsHistoryModal";
import { useProgramStatusAuditLog } from "@/hooks/workflow/useProgramStatusAuditLog";

export const PatientProgramStatusTable = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProgramStatusId, setSelectedProgramStatusId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedStatusForHistory, setSelectedStatusForHistory] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const { user, token } = useAuth();
  const {
    programStatuses,
    totalCount,
    loading,
    error,
    currentPage,
    entriesPerPage,
    setCurrentPage,
    setEntriesPerPage,
    refetch
  } = useProgramStatuses();
  
  const { programStatusAuditLogs, auditLogsLoading } = useProgramStatusAuditLog(
    selectedStatusForHistory?.id || null
  );

  const handleEdit = (id: number) => {
    navigate(`/admin/patient-program-status/edit/${id}`);
  };

  const handleDelete = (id: number) => {
    setSelectedProgramStatusId(id);
    setDeleteDialogOpen(true);
  };

  const handleViewHistory = (id: number, name: string) => {
    setSelectedStatusForHistory({ id, name });
    setHistoryModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedProgramStatusId || !user?.id || !token) {
      toast({
        title: "Error",
        description: "Unable to delete program status",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      await workflowService.deleteProgramStatus(user.id, selectedProgramStatusId, token);
      
      toast({
        title: "Success",
        description: "Program status deleted successfully",
      });
      
      setDeleteDialogOpen(false);
      setSelectedProgramStatusId(null);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete program status",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const totalPages = Math.ceil(totalCount / entriesPerPage);
  
  const filteredStatuses = programStatuses.filter((status) =>
    status.program_status.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEntriesPerPageChange = (value: string) => {
    setEntriesPerPage(Number(value));
  };

  if (error) {
    return (
      <div className="p-4 text-destructive">
        Error loading program statuses: {error}
      </div>
    );
  }

  return (
    <>
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the program status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Prevent Radix AlertDialog from auto-closing so the loader is visible
                e.preventDefault();
                void confirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting...
                </span>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Input 
            className="w-64" 
            placeholder="Search by Status Name" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show</span>
          <Select value={String(entriesPerPage)} onValueChange={handleEntriesPerPageChange}>
            <SelectTrigger className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">Entries</span>
        </div>
      </div>

      <div id="table-admin-program-status-container" className="rounded-md border">
        <Table id="table-admin-program-status">
          <TableHeader>
            <TableRow id="table-admin-program-status-header-row">
              <TableHead id="header-cell-status-name">Status Name</TableHead>
              <TableHead id="header-cell-created-date">Created Date</TableHead>
              <TableHead id="header-cell-actions" className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody id="table-admin-program-status-body">
            {loading ? (
              <TableRow id="row-program-status-loading">
                <TableCell colSpan={3} className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredStatuses.length === 0 ? (
              <TableRow id="row-program-status-empty">
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  No program statuses found
                </TableCell>
              </TableRow>
            ) : (
              filteredStatuses.map((status) => (
                <TableRow key={status.program_status_id} id={`row-program-status-${status.program_status_id}`}>
                  <TableCell id={`cell-program-status-${status.program_status_id}-name`} className="font-medium">
                    {status.program_status}
                  </TableCell>
                  <TableCell id={`cell-program-status-${status.program_status_id}-created`}>{formatDate(status.created_datetime)}</TableCell>
                  <TableCell id={`cell-program-status-${status.program_status_id}-actions`} className="text-right">
                    <div className="flex justify-end gap-2">
                      <IconButtonWithTooltip
                        id={`btn-program-status-${status.program_status_id}-history`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewHistory(status.program_status_id, status.program_status)}
                        tooltip="View History"
                        icon={<History className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-program-status-${status.program_status_id}-edit`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(status.program_status_id)}
                        tooltip="Edit"
                        icon={<Edit className="h-4 w-4" />}
                      />
                      <IconButtonWithTooltip
                        id={`btn-program-status-${status.program_status_id}-delete`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(status.program_status_id)}
                        className="text-destructive hover:text-destructive"
                        disabled={isDeleting}
                        tooltip="Delete"
                        icon={<Trash2 className="h-4 w-4" />}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          Showing {totalCount === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1} to{" "}
          {Math.min(currentPage * entriesPerPage, totalCount)} of {totalCount} entries
        </div>
        
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }
              
              return (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    onClick={() => setCurrentPage(pageNumber)}
                    isActive={currentPage === pageNumber}
                    className="cursor-pointer"
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <PaginationItem>
                <PaginationEllipsis />
              </PaginationItem>
            )}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
      </div>

      <OperationsHistoryModal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        entityName={selectedStatusForHistory?.name || ''}
        entityType="Patient Program Status"
        auditLogs={programStatusAuditLogs}
        loading={auditLogsLoading}
      />
    </>
  );
};